import { serializeError } from "serialize-error";
import { createApi } from "@reduxjs/toolkit/query/react";
import { supabase, supabaseBaseQuery, type Database } from "../../../supabase";
import type { UpdateUserProfilePayload } from "../types/usersApiTypes";

const usersApi = createApi({
  reducerPath: "api.Users",
  baseQuery: supabaseBaseQuery,
  tagTypes: ["Users"],
  endpoints: (build) => ({
    getCurrentUser: build.query<Database["public"]["Tables"]["users"]["Row"], void>({
      queryFn: async () => {
        const { data: authData, error: authError } = await supabase.auth.getUser();

        if (authError) return { error: serializeError(authError) };

        const userId = authData.user.id;

        const { data: user, error: fetchError } = await supabase
          .from("users")
          .select("*")
          .eq("id", userId)
          .single();

        if (fetchError) return { error: serializeError(fetchError) };

        return {
          data: user,
        };
      },

      providesTags: ["Users"],
    }),

    updateUserProfile: build.mutation<void, UpdateUserProfilePayload>({
      queryFn: async ({ useCases, jobTitle, companyName, fullName, avatarUrl }) => {
        const { error } = await supabase.rpc("update_user_profile", {
          p_use_cases: useCases?.join(","),
          p_job_title: jobTitle,
          p_company_name: companyName,
          p_full_name: fullName,
          p_avatar_url: avatarUrl,
        });

        if (error) return { error: serializeError(error) };

        return { data: undefined };
      },
      invalidatesTags: ["Users"],
    }),

    completeUserSignup: build.mutation<void, void>({
      queryFn: async () => {
        const { error } = await supabase.rpc("complete_user_signup");

        if (error) return { error: serializeError(error) };

        return { data: undefined };
      },
      invalidatesTags: ["Users"],
    }),
  }),
});

const { useGetCurrentUserQuery, useUpdateUserProfileMutation, useCompleteUserSignupMutation } =
  usersApi;

export {
  usersApi,
  useGetCurrentUserQuery,
  useUpdateUserProfileMutation,
  useCompleteUserSignupMutation,
};
